<script setup lang="ts">
import { computed } from 'vue';
import type { SidebarChildItem, SidebarMenuItem } from './sidebarMenu';

const props = defineProps<{
    item: SidebarMenuItem;
    expanded: boolean;
    collapsed: boolean;
    activeRoute?: string;
}>();

const emit = defineEmits<{
    (e: 'toggle', name: string): void;
    (e: 'navigate', item: SidebarMenuItem | SidebarChildItem): void;
}>();

const hasChildren = computed(() => !!props.item.children?.length);
const visibleChildren = computed(() => props.item.children?.filter((child) => !child.hidden) ?? []);

const getHref = (item: SidebarMenuItem | SidebarChildItem) => item.href || item.route;
const isExternal = (item: SidebarMenuItem | SidebarChildItem) => !!item.href;

const isLink = computed(() => !hasChildren.value && !!getHref(props.item));

// ctrl/cmd/shift/alt + click or a non-primary button (e.g. wheel click): let the browser
// handle the anchor natively so the route opens in a new tab/window.
const isModifiedClick = (event: MouseEvent) =>
    event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0;

const handleClick = (item: SidebarMenuItem | SidebarChildItem, event: MouseEvent) => {
    if (getHref(item) && (isModifiedClick(event) || isExternal(item))) {
        return;
    }
    event.stopPropagation();
    event.preventDefault();
    emit('navigate', item);
};

const onItemClick = (event: MouseEvent) => {
    if (hasChildren.value) {
        event.stopPropagation();
        event.preventDefault();
        emit('toggle', props.item.name);
        return;
    }
    handleClick(props.item, event);
};
</script>

<template>
    <div v-if="!hasChildren || visibleChildren.length" class="menu-item" :class="{ collapsed }">
        <component
            :is="isLink ? 'a' : 'button'"
            v-tippy="{ content: item.name, placement: 'right', boundary: 'window', onShow: () => collapsed }"
            :type="isLink ? undefined : 'button'"
            :href="isLink ? getHref(item) : undefined"
            :target="isExternal(item) ? '_blank' : undefined"
            :rel="isExternal(item) ? 'noopener noreferrer' : undefined"
            class="section-item"
            role="menuitem"
            :class="{
                expanded: hasChildren && expanded,
                active: !hasChildren && item.route?.length && item.route == activeRoute
            }"
            :aria-expanded="hasChildren ? expanded : undefined"
            :aria-current="!hasChildren && item.route?.length && item.route == activeRoute ? 'page' : undefined"
            @click="onItemClick"
        >
            <div class="icon-container">
                <font-awesome-icon :icon="item.icon" />
            </div>
            <span v-if="!collapsed">{{ item.name }}</span>
            <font-awesome-icon
                v-if="hasChildren && !collapsed"
                class="ml-auto chevron"
                :class="{ open: expanded }"
                :icon="['far', 'chevron-down']"
            />
        </component>

        <div v-if="hasChildren && !collapsed" class="submenu" :class="{ open: expanded }">
            <ul class="submenu-inner">
                <li
                    v-for="child in visibleChildren"
                    :key="child.name"
                    class="submenu-item"
                    :class="{ active: child.route?.length && child.route == activeRoute }"
                >
                    <component
                        :is="getHref(child) ? 'a' : 'button'"
                        :type="getHref(child) ? undefined : 'button'"
                        :href="getHref(child)"
                        :target="isExternal(child) ? '_blank' : undefined"
                        :rel="isExternal(child) ? 'noopener noreferrer' : undefined"
                        class="submenu-link"
                        role="menuitem"
                        :aria-current="child.route?.length && child.route == activeRoute ? 'page' : undefined"
                        @click="handleClick(child, $event)"
                    >
                        {{ child.name }}
                    </component>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped lang="scss">
@import 'styles/utilities/functions';

%hover-state {
    box-shadow: inset 0 0 0 1px get-color('gray-light');
    background: get-color('gray-lightest');
}

%active-state {
    font-weight: 500;
    box-shadow: inset 0 0 0 1px get-color('purple-light');
    background: get-color('purple-lightest');
}

%focus-state {
    outline: 2px solid get-color('purple');
    outline-offset: -2px;
}

.menu-item {
    .section-item {
        display: flex;
        align-items: center;
        width: 100%;
        padding: 0.5rem 1rem;
        gap: 0.625rem;
        border-radius: 0.625rem;
        border: none;
        background: none;
        color: get-color('purple-dark');
        text-decoration: none;
        cursor: pointer;

        &.active {
            @extend %active-state;
        }
        &:hover {
            @extend %hover-state;
        }
        &:focus-visible {
            @extend %focus-state;
        }

        .chevron {
            transition: transform 0.2s ease;
            &.open {
                transform: rotate(180deg);
            }
        }

        .icon-container {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 1.25rem;
            height: 1.25rem;
        }
    }

    .submenu {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.25s ease;
        &.open {
            grid-template-rows: 1fr;
        }
    }
    .submenu-inner {
        overflow: hidden;
        list-style: none;
        margin: 0;
        padding: 0;
    }
    .submenu-item {
        position: relative;
        display: flex;
        align-items: center;
        border-radius: 0.625rem;
        cursor: pointer;

        // Continuous vertical connector line (each item draws its own segment).
        &::before {
            content: '';
            position: absolute;
            left: 1.4rem;
            top: 0;
            bottom: 0;
            width: 1px;
            background: get-color('gray-light');
            pointer-events: none;
        }
        &:first-child::before {
            top: 50%;
        }
        &:last-child::before {
            bottom: 50%;
        }
        // Node dot centered on the line.
        &::after {
            content: '';
            position: absolute;
            left: calc(1.4rem - 3px);
            top: 50%;
            transform: translateY(-50%);
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: get-color('gray');
            pointer-events: none;
        }
        &.active {
            @extend %active-state;
            &::after {
                background: get-color('purple');
            }
        }
        &:hover {
            @extend %hover-state;
        }
    }

    .submenu-link {
        padding: 0.45rem 0.5rem 0.45rem 2.4rem;
        color: inherit;
        width: 100%;
        &:hover {
            text-decoration: none;
        }
        &:focus-visible {
            @extend %focus-state;
        }
    }

    &.collapsed .section-item {
        justify-content: center;
        margin: 0 auto;
        width: fit-content;
    }
}
</style>
